import { useEffect, useRef, useState } from 'react';
import '../styles/curved-ribbon.css';

const CurvedRibbon = () => {
    const measureRef = useRef<SVGTextElement>(null);
    const textPathRef = useRef<SVGTextPathElement>(null);
    const [unitWidth, setUnitWidth] = useState(0);
    const [repeats, setRepeats] = useState(4);

    const services = [
        'Mobile Apps',
        'Desktop Software',
        'Webentwicklung',
        'Progressive Web Apps',
        'Backend & APIs',
        'Server Administration',
        'UI/UX Design',
        'Cloud Solutions',
        'IT-Consulting',
        'DevOps',
        'E-Commerce',
        'SEO Optimierung'
    ];

    // Construct the unit string with simple spaces instead of complex tspans/dx
    // Using non-breaking spaces to ensure spacing is preserved
    const separator = ' ✦ ';
    const unitText = services.join(separator) + separator;

    useEffect(() => {
        const measure = async () => {
            await document.fonts.ready;
            
            if (measureRef.current) {
                try {
                    const width = measureRef.current.getComputedTextLength();
                    if (width > 0) {
                        setUnitWidth(width);
                        // Calculate how many repeats we need to cover the path (approx 1500px) plus buffer
                        // We need to cover at least: Screen Width + Unit Width (for the scroll)
                        // Let's be safe and cover 4000px or more
                        const needed = Math.ceil(4000 / width) + 2;
                        setRepeats(needed);
                    }
                } catch (e) {
                    console.warn("Could not measure text length", e);
                }
            }
        };
        
        measure();
        window.addEventListener('resize', measure);
        return () => window.removeEventListener('resize', measure);
    }, [unitText]);

    useEffect(() => {
        const node = textPathRef.current;
        if (!node || unitWidth <= 0) return;

        let offset = 0;
        let animationFrameId: number;
        let lastTime = 0;
        const speed = 30; // pixels per second

        const animate = (time: number) => {
            if (lastTime === 0) lastTime = time;
            const delta = (time - lastTime) / 1000;
            lastTime = time;

            offset -= speed * delta;
            
            // Seamless loop reset:
            // When we have scrolled one full unit width, we reset by adding unitWidth.
            // Since the string is periodic, the visual state at (offset) and (offset + unitWidth) is identical.
            if (Math.abs(offset) >= unitWidth) {
                offset += unitWidth;
            }
            
            node.setAttribute('startOffset', `${offset}`);
            animationFrameId = requestAnimationFrame(animate);
        };

        animationFrameId = requestAnimationFrame(animate);

        return () => {
            cancelAnimationFrame(animationFrameId);
        };
    }, [unitWidth]);

    return (
        <div className="curved-ribbon-container">
            <div className="relative w-full">
                <svg
                    className="curved-ribbon-svg"
                    viewBox="0 0 1440 160"
                    preserveAspectRatio="xMidYMax slice"
                >
                    <defs>
                        <linearGradient id="ribbonGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                            <stop offset="0%" stopColor="#0A8370" />
                            <stop offset="25%" stopColor="#0A8370" />
                            <stop offset="75%" stopColor="#0a835dff" />
                            <stop offset="100%" stopColor="#77F76C" />
                        </linearGradient>
                        <filter id="ribbonNoise">
                            <feTurbulence type="fractalNoise" baseFrequency="0.8" numOctaves="3" stitchTiles="stitch" result="noise"/>
                            <feColorMatrix type="matrix" values="1 0 0 0 0  0 1 0 0 0  0 0 1 0 0  0 0 0 0.15 0" in="noise" result="softNoise"/>
                            <feComposite operator="in" in="softNoise" in2="SourceGraphic" result="clippedNoise"/>
                            <feBlend mode="overlay" in="clippedNoise" in2="SourceGraphic"/>
                        </filter>
                        <path
                            id="curvePath"
                            d="M0,60 C480,120 960,0 1440,60"
                        />
                    </defs>

                    <path
                        d="M0,90 C480,150 960,30 1440,90 V160 H0 Z"
                        fill="#000000"
                    />

                    <path
                        d="M0,40 C480,100 960,-20 1440,40 L1440,90 C960,30 480,150 0,90 Z"
                        fill="url(#ribbonGradient)"
                        filter="url(#ribbonNoise)"
                        stroke="#000000"
                        strokeWidth="2"
                    />

                    {/* Hidden text element for precise measurement of one unit */}
                    <text
                        ref={measureRef}
                        className="curved-ribbon-text opacity-0"
                        x="-10000"
                        y="-10000"
                    >
                        {unitText}
                    </text>

                    {unitWidth > 0 && (
                        <text className="curved-ribbon-text" fill="#FFF" dy="10">
                            <textPath
                                href="#curvePath"
                                startOffset="0"
                                ref={textPathRef}
                            >
                                {Array(repeats).fill(unitText).join('')}
                            </textPath>
                        </text>
                    )}
                </svg>
            </div>
        </div>
    );
};

export default CurvedRibbon;
